import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { X, Ruler, User } from 'lucide-react'
import { Button } from '../common'
import customerService from '../../services/customerService'

function MeasurementForm({ 
  isOpen, 
  editingMeasurement, 
  onClose, 
  onSubmit, 
  isLoading,
  preselectedCustomerId = null,
  preselectedCustomerName = null
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm()
  const [customers, setCustomers] = useState([])
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)

  // Fetch customers for dropdown
  const fetchCustomers = async () => {
    try {
      setIsLoadingCustomers(true)
      const response = await customerService.getAll({ limit: 100 })
      setCustomers(response.data.customers || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    } finally {
      setIsLoadingCustomers(false)
    }
  }

      // Reset form when editing measurement changes
      useEffect(() => {
        if (editingMeasurement) {
          reset({
            id: editingMeasurement.id,
            customerId: editingMeasurement.customerId || '',
            measurementType: editingMeasurement.measurementType || 'Standard',
            length: editingMeasurement.length || '',
            upperchest: editingMeasurement.upperchest || '',
            chest: editingMeasurement.chest || '',
            dartPoint: editingMeasurement.dartPoint || '',
            waist: editingMeasurement.waist || '',
            sleeveFull: editingMeasurement.sleeveFull || '',
            sleeveHalf: editingMeasurement.sleeveHalf || '',
            sleeveShort: editingMeasurement.sleeveShort || '',
            sleeveRound: editingMeasurement.sleeveRound || '',
            biceps: editingMeasurement.biceps || '',
            shoulder: editingMeasurement.shoulder || '',
            armHole: editingMeasurement.armHole || '',
            hip: editingMeasurement.hip || '',
            neckFront: editingMeasurement.neckFront || '',
            neckBack: editingMeasurement.neckBack || '',
            pentLength: editingMeasurement.pentLength || '',
            pentWaist: editingMeasurement.pentWaist || '',
            thighRound: editingMeasurement.thighRound || '',
            kneeRound: editingMeasurement.kneeRound || '',
            ankleRound: editingMeasurement.ankleRound || '',
            // Keep old fields for backward compatibility
            bust: editingMeasurement.bust || '',
            sleeveLength: editingMeasurement.sleeveLength || '',
            thigh: editingMeasurement.thigh || '',
            knee: editingMeasurement.knee || '',
            bottom: editingMeasurement.bottom || '',
            neck: editingMeasurement.neck || '',
            backWidth: editingMeasurement.backWidth || '',
            frontWidth: editingMeasurement.frontWidth || '',
            notes: editingMeasurement.notes || ''
          })
        } else {
          reset({
            customerId: preselectedCustomerId || '',
            measurementType: 'Standard',
            length: '',
            upperchest: '',
            chest: '',
            dartPoint: '',
            waist: '',
            sleeveFull: '',
            sleeveHalf: '',
            sleeveShort: '',
            sleeveRound: '',
            biceps: '',
            shoulder: '',
            armHole: '',
            hip: '',
            neckFront: '',
            neckBack: '',
            pentLength: '',
            pentWaist: '',
            thighRound: '',
            kneeRound: '',
            ankleRound: '',
            // Keep old fields for backward compatibility
            bust: '',
            sleeveLength: '',
            thigh: '',
            knee: '',
            bottom: '',
            neck: '',
            backWidth: '',
            frontWidth: '',
            notes: ''
          })
        }
      }, [editingMeasurement, reset])

  // Fetch customers when modal opens (only if no preselected customer)
  useEffect(() => {
    if (isOpen && !preselectedCustomerId) {
      fetchCustomers()
    }
  }, [isOpen, preselectedCustomerId])

  // Update customerId when preselectedCustomerId changes
  useEffect(() => {
    if (preselectedCustomerId && !editingMeasurement) {
      reset({
        ...(preselectedCustomerId ? { customerId: preselectedCustomerId } : {})
      })
    }
  }, [preselectedCustomerId, editingMeasurement, reset])

  const handleSubmitForm = (data) => {
    console.log('MeasurementForm - Submitting data:', data)
    console.log('MeasurementForm - Editing measurement:', editingMeasurement)
    console.log('MeasurementForm - Has ID:', !!data.id)
    onSubmit(data)
  }

  const handleClose = () => {
    reset({
      customerId: '',
      measurementType: 'Standard',
      length: '',
      upperchest: '',
      chest: '',
      dartPoint: '',
      waist: '',
      sleeveFull: '',
      sleeveHalf: '',
      sleeveShort: '',
      sleeveRound: '',
      biceps: '',
      shoulder: '',
      armHole: '',
      hip: '',
      neckFront: '',
      neckBack: '',
      pentLength: '',
      pentWaist: '',
      thighRound: '',
      kneeRound: '',
      ankleRound: '',
      // Keep old fields for backward compatibility
      bust: '',
      sleeveLength: '',
      thigh: '',
      knee: '',
      bottom: '',
      neck: '',
      backWidth: '',
      frontWidth: '',
      notes: ''
    })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Ruler className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    {editingMeasurement ? 'Edit Measurement' : 'Add New Measurement'}
                  </h3>
                  <p className="text-base text-secondary-500">
                    {editingMeasurement ? 'Update measurement details' : 'Enter measurement information'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-4">
              {/* Hidden ID field for updates */}
              <input type="hidden" {...register('id')} />
              
              <div className="modal-body max-h-[28rem] overflow-y-auto">
                {/* Customer Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {preselectedCustomerId ? (
                    // Show customer name as read-only when preselected
                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Customer *
                      </label>
                      <div className="input bg-secondary-50 text-secondary-700 flex items-center">
                        <User className="h-4 w-4 mr-2 text-secondary-500" />
                        <span>{preselectedCustomerName || `Customer ID: ${preselectedCustomerId}`}</span>
                      </div>
                      <input type="hidden" {...register('customerId', { value: preselectedCustomerId })} />
                    </div>
                  ) : (
                    // Show customer dropdown when not preselected
                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Customer *
                      </label>
                      <select 
                        {...register('customerId', { required: 'Customer is required' })} 
                        className="input"
                        disabled={isLoadingCustomers}
                      >
                        <option value="">{isLoadingCustomers ? 'Loading customers...' : 'Select Customer'}</option>
                        {customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.firstName} {customer.lastName} - {customer.phone}
                          </option>
                        ))}
                      </select>
                      {errors.customerId && (
                        <p className="text-danger-600 text-base mt-1">{errors.customerId.message}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Measurement Type
                    </label>
                    <select {...register('measurementType')} className="input">
                      <option value="Standard">Standard</option>
                      <option value="Formal">Formal</option>
                      <option value="Casual">Casual</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>
                </div>

                {/* Measurements Grid */}
                <div className="space-y-4">
                  {/* Upper Body Measurements */}
                  <div>
                    <h4 className="text-base font-medium text-secondary-900 mb-3 border-b border-secondary-200 pb-2">Upper Body Measurements</h4>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Length</label>
                        <input {...register('length')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Upper Chest</label>
                        <input {...register('upperchest')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Chest</label>
                        <input {...register('chest')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Dart Point</label>
                        <input {...register('dartPoint')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Waist</label>
                        <input {...register('waist')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Shoulder</label>
                        <input {...register('shoulder')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Arm Hole</label>
                        <input {...register('armHole')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Biceps</label>
                        <input {...register('biceps')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Hip</label>
                        <input {...register('hip')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Neck Front</label>
                        <input {...register('neckFront')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Neck Back</label>
                        <input {...register('neckBack')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                    </div>
                  </div>

                  {/* Sleeve Measurements */}
                  <div>
                    <h4 className="text-base font-medium text-secondary-900 mb-3 border-b border-secondary-200 pb-2">Sleeve Measurements</h4>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Sleeve Full</label>
                        <input {...register('sleeveFull')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Sleeve Half</label>
                        <input {...register('sleeveHalf')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Sleeve Short</label>
                        <input {...register('sleeveShort')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Sleeve Round</label>
                        <input {...register('sleeveRound')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                    </div>
                  </div>

                  {/* Lower Body Measurements */}
                  <div>
                    <h4 className="text-base font-medium text-secondary-900 mb-3 border-b border-secondary-200 pb-2">Lower Body Measurements</h4>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Pent Length</label>
                        <input {...register('pentLength')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Pent Waist</label>
                        <input {...register('pentWaist')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Thigh Round</label>
                        <input {...register('thighRound')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Knee Round</label>
                        <input {...register('kneeRound')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Ankle Round</label>
                        <input {...register('ankleRound')} type="number" step="0.01" className="input text-base" placeholder="0.00" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="mt-4">
                  <label className="block text-base font-medium text-secondary-700 mb-1">Notes</label>
                  <textarea {...register('notes')} className="input text-base" rows={2} placeholder="Additional notes..." />
                </div>
              </div>

              <div className="modal-footer flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : editingMeasurement ? 'Update Measurement' : 'Create Measurement'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeasurementForm
